import * as React from 'react';
import { useEffect, useState } from 'react';
import { Router } from 'react-router';
import AppHeader from '../react/features/AppHeader';
import ClippyTrigger from '../react/features/Clippy/components/Trigger/lazy';
import HeaderBroadcast from '../react/features/HeaderBroadcast';
import { LicenseWillExpireWarning, TrialExpiredWarning } from '../react/features/LicenseExpiredWarnings';
import Navigation from '../react/features/Navigation';
import { TestDriveModeContainer } from '../react/features/TestDriveMode';
import { TOUR_INTRO } from '../react/intercom/intercom-data-targets';
import { CompanyDesignProvider } from '../react/services/CompanyDesignProvider';
import { isFeatureEnabled } from '../react/services/Features';
import * as keyEventStackService from '../react/services/KeyEventStack';
import AppRoutes from '../routing/Routes';
import history, { globalClickHandler } from '../routing/history';
import AppShell from './AppShell';

const useGlobalLinkClickListener = () => {
    useEffect(() => {
        document.addEventListener('click', globalClickHandler);
        return () => document.removeEventListener('click', globalClickHandler);
    }, []);
};

const useNavigationToggleState = () => {
    const [showNavigation, setShowNavigation] = useState(false);

    useEffect(() => {
        if (showNavigation) {
            const className = 'main-navigation-open';
            document.body.classList.add(className);

            const keyEvent = {
                onKey: (event: unknown) => {
                    if (keyEventStackService.isEsc(event)) {
                        setShowNavigation(false);
                        return false;
                    }
                },
            };
            keyEventStackService.register(keyEvent);

            const unlisten = history?.listen(() => setShowNavigation(false));

            return () => {
                document.body.classList.remove(className);
                keyEventStackService.deregister(keyEvent);
                unlisten?.();
            };
        }
    }, [showNavigation]);

    return { showNavigation, setShowNavigation };
};

const App: React.FC = () => {
    useGlobalLinkClickListener();
    const { showNavigation, setShowNavigation } = useNavigationToggleState();

    return (
        <Router history={history!}>
            <CompanyDesignProvider>
                {isFeatureEnabled('FEATURE_layoutRefresh') ? (
                    <AppShell showNavigation={showNavigation} setShowNavigation={setShowNavigation}>
                        <AppRoutes />
                    </AppShell>
                ) : (
                    <>
                        <HeaderBroadcast />
                        <TestDriveModeContainer />
                        <AppHeader toggleNavigation={() => setShowNavigation(!showNavigation)} />

                        <div className="main-wrapper-inner">
                            <Navigation closeNavigation={() => setShowNavigation(false)} />
                            <main role="main" className="main-wrapper-content">
                                <div className="page" data-intercom-target={TOUR_INTRO.HOME_PAGE}>
                                    <section className="global_warnings">
                                        <TrialExpiredWarning />
                                        <LicenseWillExpireWarning />
                                    </section>
                                    <AppRoutes />
                                </div>
                            </main>
                        </div>
                    </>
                )}
            </CompanyDesignProvider>

            <aside id="clippy">
                <ClippyTrigger />
            </aside>
        </Router>
    );
};

export default App;
